import { useState, useCallback, useEffect, useRef } from "react";

import { config } from "../../config";
import { useHttpClient } from "../Hooks/http-hook";
import dedupeStoreFetch from "../util/dedupe-store-fetch";

const useStoreSections = () => {
    const [storeSections, setStoreSections] = useState([]);
    const [isUpdatedStoreSections, setIsUpdatedStoreSections] = useState(false);
    const [HideStoreSectionsSkeleton, setHideStoreSectionsSkeleton] = useState(false);
    const [storeSectionsUsername, setStoreSectionsUsername] = useState('');
    const { error, sendRequest, clearError } = useHttpClient();
    const cacheRef = useRef({
        isUpdatedStoreSections: false,
        storeSectionsUsername: '',
        storeSections: [],
    });
    const pendingFetchesRef = useRef(new Map());

    cacheRef.current = {
        isUpdatedStoreSections,
        storeSectionsUsername,
        storeSections,
    };

    const updateStoreSections = (newCategories) => {
        setStoreSections(newCategories);
        setIsUpdatedStoreSections(true);
    };

    const fetchStoreSections = useCallback(async (store, { force = false } = {}) => {
        return dedupeStoreFetch({
            key: store,
            force,
            cacheRef,
            pendingRef: pendingFetchesRef,
            isCacheHit: (cache) => (
                cache.isUpdatedStoreSections && cache.storeSectionsUsername === store
            ),
            getCachedValue: (cache) => cache.storeSections,
            runRequest: async () => {
                try {
                    const responseData = await sendRequest(
                        `${config.API_URL}/storeSections/${store}`
                    );
                    clearError();
                    updateStoreSections(responseData);
                    setStoreSectionsUsername(store);
                    return responseData;
                } catch (err) {
                    setIsUpdatedStoreSections(true);
                    setStoreSectionsUsername(store);
                    console.error(err);
                    return null;
                }
            },
        });
    }, [sendRequest, clearError]);

    const StoreSectionDeletedHandler = useCallback((deletedStoreSectionId) => {
        setStoreSections((prevStoreSections) =>
            prevStoreSections.filter(
                (section) => section._id !== deletedStoreSectionId
            )
        );
    }, [setStoreSections]);

    const reloadStoreSections = useCallback(
        (store) => fetchStoreSections(store, { force: true }),
        [fetchStoreSections]
    );

    const removeStoreSections = useCallback(() => {
        pendingFetchesRef.current.clear();
        setStoreSections(null);
        setStoreSectionsUsername('');
        setIsUpdatedStoreSections(false);
        setHideStoreSectionsSkeleton(false);
        clearError();
    }, [clearError]);

    useEffect(() => {
        removeStoreSections();
    }, []);

    return {
        storeSections,
        updateStoreSections,
        isUpdatedStoreSections,
        fetchStoreSections,
        reloadStoreSections,
        HideStoreSectionsSkeleton,
        StoreSectionDeletedHandler,
        storeSectionsError: error,
        setHideStoreSectionsSkeleton,
        removeStoreSections
    };
}

export default useStoreSections;
